import { AreaUnit } from '@search/filter-enums/enums/AreaUnit';
import { convertAreaToGQL } from './gql/convertToGQL';

import {
    formatArea as formatAreaGQL,
    formatAreaUnit as formatAreaUnitGQL,
    formatAreaUnitFor as formatAreaUnitForGQL,
    formatAreaRange as formatAreaRangeGQL
} from './gql/formatArea';

type Area = {
    value: number;
    unit: AreaUnit;
};

/**
 * Возвращает русскоязычную запись единиц площади
 * @param {Area} area
 * @param {number} area.value
 * @param {AreaUnit} area.unit
 * @returns {string}
 * @example
 * formatAreaUnit({ value: 1, unit: AreaUnit.ARE }) -> 'сотка'
 * formatAreaUnit({ value: 5, unit: AreaUnit.HECTARE }) -> 'гектаров'
 * formatAreaUnit({ value: 100, unit: AreaUnit.METER }) -> 'м²'
 */
export const formatAreaUnit = (area: Area) => formatAreaUnitGQL(convertAreaToGQL(area));

/**
 * Возвращает русскоязычную запись единиц площади в винительном падеже,
 * как например, в предложении "за 5 соток"
 * @param {Area} area
 * @param {number} area.value
 * @param {AreaUnit} area.unit
 * @returns {string}
 * @example
 * formatAreaUnitFor({ value: 1, unit: AreaUnit.ARE }) -> 'сотку'
 * formatAreaUnitFor({ value: 5, unit: AreaUnit.HECTARE }) -> 'гектаров'
 * formatAreaUnitFor({ value: 100, unit: AreaUnit.METER }) -> 'м²'
 */
export const formatAreaUnitFor = (area: Area) => formatAreaUnitForGQL(convertAreaToGQL(area));

/**
 * Возвращает человекопонятную запись площади
 * @param {Area} area
 * @param {boolean} [hasUnit = false]
 * @returns {string}
 * @example
 * formatArea({ value: 2.34, unit: AreaUnit.HECTARE }) -> '2,3 гектара'
 */
export const formatArea = (area: Area, hasUnit: boolean = true) => formatAreaGQL(convertAreaToGQL(area), hasUnit);

/**
 * Возвращает человекопонятную запись диапазона площадей
 * @param {Object} range
 * @param {Area} range.minArea
 * @param {Area} range.maxArea
 * @returns {string}
 * @example
 * formatAreaRange(
 *     minArea: {
 *         value: 1.23,
 *         unit: AreaUnit.ARE
 *     },
 *     maxArea: {
 *         value: 2.34,
 *         unit: AreaUnit.ARE
 *     }
 * ) -> '1,2 - 2,3 сотки'
 */
export const formatAreaRange = ({ minArea, maxArea }: {
    minArea: Area;
    maxArea: Area;
}) => formatAreaRangeGQL({ minArea: convertAreaToGQL(minArea), maxArea: convertAreaToGQL(maxArea) });
